import React, { ChangeEvent, useState } from 'react';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Button, Input } from 'antd';
// Api
import { EDIT_CAMPAIGN_SETTINGS } from 'api/campaignV2/mutations';
// Types
import {
  GetCampaignsV2,
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2Variables,
} from 'api/campaignV2/types/GetCampaignsV2';
import {
  EditCampaignSettings,
  EditCampaignSettingsVariables,
} from 'api/campaignV2/types/EditCampaignSettings';
// Ui
import { successNotification, errorNotification } from 'ui/Notification';
// Style
import styles from './ChangeCampaignMaxBudget.module.scss';

type ChangeCampaignMaxBudgetProps = {
  record: GetCampaignsV2_getCampaigns_entities;
  refetch: (
    variables?: Partial<GetCampaignsV2Variables> | undefined
  ) => Promise<ApolloQueryResult<GetCampaignsV2>>;
};

const ChangeCampaignMaxBudget = ({
  record,
  refetch,
}: ChangeCampaignMaxBudgetProps): JSX.Element | null => {
  const [budgetIncrease, setBudgetIncrease] = useState<string>('');

  const [editCampaignSettings] = useMutation<
    EditCampaignSettings,
    EditCampaignSettingsVariables
  >(EDIT_CAMPAIGN_SETTINGS);

  const handleMaxBudgetChange = (newValue: ChangeEvent<HTMLInputElement>) => {
    const newBudgetValue = newValue.target.value;

    const positiveOrNegativeNumber = /^[-+]?\d+$/;
    const isValid = positiveOrNegativeNumber.test(newBudgetValue);
    if (isValid) {
      setBudgetIncrease(newBudgetValue);
    }
  };

  const handleUpdateBudget = async () => {
    if (record?.id && Number(budgetIncrease)) {
      try {
        const { data } = await editCampaignSettings({
          variables: {
            input: {
              campaignId: record?.id,
              maxBudget: record?.estimatedBudget
                ? record?.estimatedBudget + Number(budgetIncrease)
                : null,
            },
          },
        });
        if (data?.editCampaignSettings.id) {
          successNotification('Campaign max budget has been updated');
        }

        refetch();
      } catch (error) {
        console.error('Error updating max budget', error);
        errorNotification('Error updating max budget');
      }
    } else {
      errorNotification('Impossible max budget value');
    }
  };

  return (
    <div className={styles.root}>
      <p className={styles.label}>Max Budget Change</p>
      <div className={styles.budgetInputAndButton}>
        <Input
          onChange={handleMaxBudgetChange}
          style={{ width: 250 }}
          placeholder="$"
        />

        <Button type="primary" onClick={handleUpdateBudget}>
          Add max budget
        </Button>
      </div>
      <p className={styles.budgetExplanation}>
        A positive number adds to the max budget, a negative number subtracts
        from it.
      </p>
    </div>
  );
};

export default ChangeCampaignMaxBudget;
